import * as React from 'react'
import { ScrollView, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import AlertButton from '@components/button/AlertButton'
import SiteHeader from '../SiteHeader'
import IncidentsHomeSection from './PrestaIncidentsHomeSection'
import DraftsButton from '@components/button/DraftsButton'

import { isPrestaIncidentUnaffected } from '@utils/incidentUtils'

import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import { store } from '@utils/useSiteChangeHandler'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'
import useDrafts from '@utils/useDrafts'

const MAIN_VERTICAL_PADDING = 24

interface Props {
  onBack?: () => void
}

const PrestaSiteScreen = (props: Props) => {
  const user = useReducer(UserStore.store, (s) => s.user)

  // screen available only for connected user
  if (!user) {
    return null
  }

  return <Screen {...props} user={user} />
}

interface ScreenProps extends Props {
  user: UserResponse
}

const Screen = ({ onBack, user }: ScreenProps) => {
  const i18n = useI18n()

  const siteInfo = useReducer(SiteStore.store, (s) => s.site!)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const status = useReducer(store, (s) => s.status!)

  const navigation = useNavigation()

  const [hasScrolled, setHasScrolled] = React.useState(false)
  const [maxHeaderHeight, setMaxHeaderHeight] = React.useState(0)
  const [maxScrollViewHeight, setMaxScrollViewHeight] = React.useState(0)
  const [maxContentHeight, setMaxContentHeight] = React.useState(0)

  const scroll = React.useRef<ScrollView>(null)

  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const draftsCount = useDrafts()[0]

  const unaffectedIncidents = React.useMemo(() => incidents.filter(isPrestaIncidentUnaffected).length, [incidents])
  const quoteWantedIncidents = React.useMemo(
    () => incidents.filter((inc) => inc.status === 'EN_ATTENTE_DE_DEVIS').length,
    [incidents]
  )

  const displayAlertButton = unaffectedIncidents > 0 || quoteWantedIncidents > 0

  const onMapPressed = () => navigation.push('/plan')

  const startButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push('/incident/start'),
  }

  const unaffectedButton = {
    label: i18n.t('screens.site.prestaAlert', {
      prenom: user.prenom,
      total:
        unaffectedIncidents > 0 && quoteWantedIncidents > 0
          ? i18n.t('screens.site.unaffected', { count: unaffectedIncidents }) +
            ' & ' +
            i18n.t('screens.site.quoteWanted', { count: quoteWantedIncidents })
          : quoteWantedIncidents > 0
          ? i18n.t('screens.site.quoteWanted', { count: quoteWantedIncidents })
          : i18n.t('screens.site.unaffected', { count: unaffectedIncidents }),
    }),
    onClick: () => navigation.push('/unaffected_incidents'),
  }

  const headerButtons: HeaderButton[] = React.useMemo(() => {
    if (!siteInfo) {
      return []
    }

    /* TODO : conditionner à la présence d'un plan */
    const buttons: HeaderButton[] = /*siteInfo.mapId*/ true
      ? [
          {
            label: i18n.t('screens.site.plan'),
            onClick: onMapPressed,
            color: 'light',
          },
        ]
      : []

    return [...buttons, startButton]
  }, [siteInfo, i18n])

  const renderBottomButton = isPhone
    ? () => (
        <BottomButton width={width}>
          <Button status="active" label={startButton.label} onClick={startButton.onClick} />
        </BottomButton>
      )
    : undefined

  return (
    <Root
      loading={status === 'loading'}
      renderFixedBottom={renderBottomButton}
      header={siteInfo ? i18n.t(`screens.site.sites.${siteInfo.codeLieu}`) : i18n.t('screens.site.title')}
      renderHeader={
        isPhone
          ? () => (
              <SiteHeader
                onLayout={(e) =>
                  e.nativeEvent.layout.height > maxHeaderHeight
                    ? setMaxHeaderHeight(e.nativeEvent.layout.height)
                    : undefined
                }
                hasScrolled={hasScrolled}
                onBack={onBack}
                onMapPressed={onMapPressed}
                actionButton={displayAlertButton ? unaffectedButton : undefined}
              />
            )
          : undefined
      }
      onLeft={onBack}
      fixed>
      <ScrollView
        ref={scroll}
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => setHasScrolled(event.nativeEvent.contentOffset.y > 0)}
        scrollEventThrottle={16}
        onLayout={(e) =>
          e.nativeEvent.layout.height > maxScrollViewHeight
            ? setMaxScrollViewHeight(e.nativeEvent.layout.height)
            : undefined
        }>
        {!isPhone && (
          <Header
            siteIcon={siteInfo.codeLieu as SiteIconName}
            title={siteInfo ? i18n.t(`screens.site.sites.${siteInfo.codeLieu}`) : i18n.t('screens.site.title')}
            buttons={headerButtons}
          />
        )}

        <MainContainer
          onLayout={(e) =>
            e.nativeEvent.layout.height > maxContentHeight
              ? setMaxContentHeight(e.nativeEvent.layout.height)
              : undefined
          }
          extraPadding={
            hasScrolled &&
            maxContentHeight > 0 &&
            maxScrollViewHeight > 0 &&
            maxContentHeight < maxScrollViewHeight + maxHeaderHeight
              ? maxHeaderHeight
              : 0
          }>
          {!isPhone && displayAlertButton && (
            <AlertButtonContainer>
              <AlertButton label={unaffectedButton.label} onClick={unaffectedButton.onClick} />
            </AlertButtonContainer>
          )}

          {draftsCount > 0 && (
            <AlertButtonContainer>
              <DraftsButton count={draftsCount} onClick={() => navigation.push('/drafts')} />
            </AlertButtonContainer>
          )}

          <IncidentsHomeSection incidents={incidents} onSeeAll={() => navigation.push('/incidents')} />
        </MainContainer>
      </ScrollView>
    </Root>
  )
}

export default PrestaSiteScreen

const MainContainer = styled(View)<{ extraPadding: number }>`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
  ${(props) => props.extraPadding > 0 && `padding-bottom: ${MAIN_VERTICAL_PADDING + props.extraPadding}px;`}
`
const AlertButtonContainer = styled(View)`
  margin-bottom: 32px;
`

const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`
